/**
 * TIRVerticalBarChart - Professional CGM-style Time in Range vertical bar chart
 *
 * Features:
 * - Title "TIR (Time in Range)" at the top
 * - Vertical stacked bar with rounded "capsule" corners showing glucose zones
 * - Right side legend with color dots, zone names, ranges, percentages, and durations
 * - Divider lines between each zone row
 * - Follows TIR Standardization v3.0 (top to bottom: Very High → High → Target → Low → Very Low)
 * - Matches design specification (Figure 2 style)
 */

import { Box, Divider, Skeleton, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { useStore, useTranslate } from 'react-admin'
import { useDeviceMetrics } from '../hooks/useDeviceMetrics'
import { useGlucoseDisplay } from '../hooks/useGlucoseDisplay'
import { GLUCOSE_THRESHOLDS, TIR_ZONE_COLORS } from '../types/dashboard'
import { SHARED_METRICS_PER_PAGE } from '../utils/paginationUtils'
import { calculateTIRStats } from '../utils/tirStats'

export interface TIRVerticalBarChartProps {
  deviceId?: string | number
  petId?: string | number
  startDate?: Date
  endDate?: Date
  height?: number
  barWidth?: number
  /** Show title above the bar chart (default: true) */
  showTitle?: boolean
  targetMin?: number
  targetMax?: number
  highMax?: number
}

interface ZoneData {
  name: string
  translationKey: string
  range: string
  color: string
  percent: number
  minutes: number
  count: number
}

// Format time from minutes to "Xh Ymin" format
const formatTime = (minutes: number, translate: (key: string) => string): string => {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  const hAbbr = translate('pos.glucose.hour_abbr')
  const mAbbr = translate('pos.glucose.minute_abbr')

  if (hours > 0 && mins > 0) {
    return `${hours}${hAbbr} ${mins}${mAbbr}`
  } else if (hours > 0) {
    return `${hours}${hAbbr}`
  } else {
    return `${mins}${mAbbr}`
  }
}

export const TIRVerticalBarChart: React.FC<TIRVerticalBarChartProps> = React.memo(
  ({
    deviceId: propDeviceId,
    petId: propPetId,
    startDate,
    endDate,
    height = 300,
    barWidth = 60,
    showTitle = true,
    targetMin,
    targetMax,
    highMax,
  }) => {
    const translate = useTranslate()
    const [storeDeviceId] = useStore<any>('selectedDeviceId')
    const [storePetId] = useStore<any>('selectedPetId')
    const { glucoseUnit, formatValue } = useGlucoseDisplay()
    const deviceId = propDeviceId ?? storeDeviceId
    const petId = propPetId ?? storePetId

    // Use the unified metrics hook
    const {
      data: metricsData = [],
      isLoading,
      error,
    } = useDeviceMetrics({
      deviceId,
      petId,
      startDate,
      endDate,
      sort: { field: 'timestamp', order: 'ASC' },
      pagination: { page: 1, perPage: SHARED_METRICS_PER_PAGE },
    })

    // Calculate zone statistics
    const zoneStats = useMemo(() => {
      const computedStats = calculateTIRStats(
        metricsData,
        startDate,
        endDate,
        targetMin,
        targetMax,
        highMax,
      )
      if (!computedStats) return null

      const total = computedStats.total || 1
      const minutesPerReading = 3 // CGM reads every 3 minutes

      // Build zone data array (ordered top to bottom per v3.0)
      // Range strings are dynamic based on glucose unit and species target range
      const effMin = targetMin ?? GLUCOSE_THRESHOLDS.TARGET_MIN
      const effMax = targetMax ?? GLUCOSE_THRESHOLDS.TARGET_MAX
      const effHigh = highMax ?? GLUCOSE_THRESHOLDS.HIGH
      const zones: ZoneData[] = [
        {
          name: translate('pos.glucose.very_high'),
          translationKey: 'pos.glucose.very_high',
          range: `> ${formatValue(effHigh)} ${glucoseUnit}`,
          color: TIR_ZONE_COLORS.veryHigh,
          percent: (computedStats.veryHighCount / total) * 100,
          minutes: computedStats.veryHighCount * minutesPerReading,
          count: computedStats.veryHighCount,
        },
        {
          name: translate('pos.glucose.high'),
          translationKey: 'pos.glucose.high',
          range: `${formatValue(effMax + 0.1)} – ${formatValue(effHigh)} ${glucoseUnit}`,
          color: TIR_ZONE_COLORS.high,
          percent: (computedStats.highCount / total) * 100,
          minutes: computedStats.highCount * minutesPerReading,
          count: computedStats.highCount,
        },
        {
          name: translate('pos.glucose.target'),
          translationKey: 'pos.glucose.target',
          range: `${formatValue(effMin)} – ${formatValue(effMax)} ${glucoseUnit}`,
          color: TIR_ZONE_COLORS.target,
          percent: (computedStats.inRangeCount / total) * 100,
          minutes: computedStats.inRangeCount * minutesPerReading,
          count: computedStats.inRangeCount,
        },
        {
          name: translate('pos.glucose.low'),
          translationKey: 'pos.glucose.low',
          range: `${formatValue(GLUCOSE_THRESHOLDS.VERY_LOW)} – ${formatValue(effMin - 0.1)} ${glucoseUnit}`,
          color: TIR_ZONE_COLORS.low,
          percent: (computedStats.lowCount / total) * 100,
          minutes: computedStats.lowCount * minutesPerReading,
          count: computedStats.lowCount,
        },
        {
          name: translate('pos.glucose.very_low'),
          translationKey: 'pos.glucose.very_low',
          range: `< ${formatValue(GLUCOSE_THRESHOLDS.VERY_LOW)} ${glucoseUnit}`,
          color: TIR_ZONE_COLORS.veryLow,
          percent: (computedStats.veryLowCount / total) * 100,
          minutes: computedStats.veryLowCount * minutesPerReading,
          count: computedStats.veryLowCount,
        },
      ]

      return {
        zones,
        total,
        tirPercent: computedStats.tir,
      }
    }, [
      metricsData,
      startDate,
      endDate,
      translate,
      glucoseUnit,
      formatValue,
      targetMin,
      targetMax,
      highMax,
    ])

    if (isLoading) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: height,
          }}
        >
          <Skeleton variant="rectangular" width={barWidth + 200} height={height} />
        </Box>
      )
    }

    if (error || !zoneStats) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: height,
          }}
        >
          <Typography color="text.secondary" variant="body2">
            {translate('pos.glucose.no_data_available')}
          </Typography>
        </Box>
      )
    }

    // Bar height for the capsule (full height when no title, or reserve space for title)
    const barHeight = showTitle ? height - 40 : height

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          // Use minHeight (not a hard height) so the legend can grow when zone
          // rows wrap on narrow viewports. On desktop the rows fit within
          // `height`, so the box stays exactly `height` — identical render.
          // A hard height + justifyContent:'center' on the legend clipped
          // "Very High"/"Very Low" off the top/bottom on phones.
          minHeight: height,
          width: '100%',
          gap: 4,
        }}
        data-testid="tir-vertical-bar-chart"
      >
        {/* Left side: Title above bar + Capsule bar */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: barWidth + 40,
          }}
        >
          {/* Title: TIR (Time in Range) - positioned above bar */}
          {showTitle && (
            <Typography
              variant="h5"
              data-testid="tir-vertical-bar-chart-title"
              sx={{
                fontSize: '1.5rem',
                fontWeight: 800,
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                mb: 2.5,
                color: 'text.primary',
                textAlign: 'center',
                whiteSpace: 'nowrap',
              }}
            >
              {translate('pos.glucose.range_distribution')}
            </Typography>
          )}

          {/* Rounded capsule vertical stacked bar */}
          <Box
            sx={{
              width: barWidth,
              height: barHeight,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: `${barWidth / 2}px`,
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            }}
          >
            {/* Site-wide standard v3.0 stacking order (top to bottom): Very High → High → Target → Low → Very Low */}
            {zoneStats.zones.map((zone, index) => (
              <Box
                key={index}
                sx={{
                  height: `${zone.percent}%`,
                  minHeight: zone.percent > 0 ? 2 : 0,
                  backgroundColor: zone.color,
                  transition: 'height 0.3s ease',
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Right side: Zone list with dividers - fills remaining space */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            flex: 1,
            py: showTitle ? 4 : 0,
          }}
        >
          {zoneStats.zones.map((zone, index) => (
            <React.Fragment key={index}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1.5,
                }}
              >
                {/* Left side: Color dot + zone name + range */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {/* Color dot indicator */}
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      backgroundColor: zone.color,
                      borderRadius: '50%',
                      flexShrink: 0,
                    }}
                  />

                  {/* Zone name and range in single line */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: 'text.primary',
                      fontSize: '0.875rem',
                    }}
                  >
                    {zone.name}{' '}
                    <Typography
                      component="span"
                      sx={{
                        color: 'text.secondary',
                        fontSize: '0.8rem',
                        fontWeight: 400,
                      }}
                    >
                      ({zone.range})
                    </Typography>
                  </Typography>
                </Box>

                {/* Right side: Percentage + duration */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      fontSize: '0.95rem',
                    }}
                  >
                    {zone.percent.toFixed(1)}%
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.8rem',
                      ml: 0.5,
                    }}
                  >
                    {formatTime(zone.minutes, translate)}
                  </Typography>
                </Box>
              </Box>

              {/* Divider between rows (not after the last item) */}
              {index < zoneStats.zones.length - 1 && (
                <Divider sx={{ borderColor: 'divider', opacity: 0.5 }} />
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    )
  },
)

export default TIRVerticalBarChart
